import { t } from '../i18n';
import { apiPath, MEDIA_UPLOAD_FILE_FIELD, type MediaAssetRecord, MediaAssetRecordSchema } from '@codot/contracts';
import { ApiError, refreshSession } from './http';

export interface UploadHandle {
  promise: Promise<MediaAssetRecord>;
  abort: () => void;
}

/**
 * `POST /media` with upload progress. fetch() cannot report request-body progress, so this one call uses
 * XMLHttpRequest (same-origin cookies, contract-validated 202 body, one transparent refresh on 401).
 */
export function uploadMedia(
  file: File,
  onProgress: (fraction: number) => void,
  name?: string,
  /** Phase 16 Step 6: create inside this library folder. */
  folderId?: string | null,
): UploadHandle {
  let xhr: XMLHttpRequest | null = null;
  let aborted = false;

  const send = () =>
    new Promise<{ status: number; body: unknown }>((resolve, reject) => {
      xhr = new XMLHttpRequest();
      xhr.open('POST', apiPath('mediaUpload'));
      xhr.withCredentials = true;
      xhr.responseType = 'json';
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
      xhr.onload = () => resolve({ status: xhr!.status, body: xhr!.response as unknown });
      xhr.onerror = () => reject(new ApiError(t('ui.uploadNetwork'), 0));
      xhr.onabort = () => reject(new ApiError(t('ui.uploadCancelled'), 0));
      const form = new FormData();
      if (name) form.append('name', name); // text fields BEFORE the file part (the API streams the file)
      if (folderId) form.append('folderId', folderId);
      form.append(MEDIA_UPLOAD_FILE_FIELD, file, file.name);
      xhr.send(form);
    });

  const promise = (async () => {
    let res = await send();
    if (res.status === 401 && !aborted && (await refreshSession())) res = await send();
    if (res.status !== 202) {
      const message = (res.body as { message?: string } | null)?.message ?? t('ui.uploadFailed', { status: res.status });
      throw new ApiError(message, res.status, res.body);
    }
    return MediaAssetRecordSchema.parse(res.body);
  })();

  return {
    promise,
    abort: () => {
      aborted = true;
      (xhr as XMLHttpRequest | null)?.abort();
    },
  };
}
